import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Alert, FormSection } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { BariPostEnrollmentDataV2 } from '@common/validation/customControls/bariPost/bariPostEnrollment/v2/bariPostEnrollmentDataV2';
import type { BariPreNurseSpecialistMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/types';
import { CustomControlCode, ProcessActivityStatus, ServiceCode } from '@common/enums';
import { findLatestProcessByServiceCode, getCustomControlData } from '@common/utils/processUtil';
import { LdlHdlRatio } from '@frontend/components/elements/LdlHdlRatio';
import { ProcessActivityAssetsDropzone } from '@frontend/components/pages/episodeCategory/customControls/processActivityAssets/ProcessActivityAssetsDropzone';
import { useProcessActivityEditContext } from '@frontend/utils';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import {
    BooleanRadioGroupFormGroup,
    DateInputFormGroup,
    NumberInputColumnRowFormGroup,
    TextAreaFormGroup,
} from '../../../processActivityFormGroupFields';
import { BariPostEnrollmentOperatie } from '../BariPostEnrollmentOperatie';

export function BariPostEnrollmentEditV2(): JSX.Element {
    const { data, onChange, processActivityId } = useProcessActivityEditContext<BariPostEnrollmentDataV2>();
    const { processes } = usePatientEpisodeCategoryContext();
    const [isPrefilled, setIsPrefilled] = useState(false);

    useEffect(() => {
        if (!processActivityId) {
            loadNurseSpecialistMedScreeningCustomControlData();
        }
    }, [processActivityId, processes]);

    return (
        <>
            <BariPostEnrollmentOperatie />
            <FormSection title="Labwaarden">
                <DateInputFormGroup
                    fieldKey="datum_lab_geprikt"
                    label="Datum lab geprikt"
                    maxDate={DateTime.now().format()}
                />
                <ProcessActivityAssetsDropzone
                    errorText="Voeg alsjeblieft bestanden met labwaarden toe"
                    label="Pre-operatieve labwaarden"
                />
                {isPrefilled && (
                    <Alert appearance="info">
                        Deze gegevens zijn afkomstig uit de medische screening. Het is mogelijk hier meer recente
                        gegevens in te vullen.
                    </Alert>
                )}
                <NumberInputColumnRowFormGroup
                    appendedLabel="mmol/mol"
                    fieldKey="hba1c"
                    isRequired={true}
                    label="HbA1c"
                    maximumFractionDigits={2}
                />
                <NumberInputColumnRowFormGroup
                    appendedLabel="mmol/l"
                    fieldKey="LDL_cholesterol"
                    isRequired={true}
                    label="LDL-cholesterol"
                    maximumFractionDigits={2}
                />
                <NumberInputColumnRowFormGroup
                    appendedLabel="mmol/l"
                    fieldKey="HDL_cholesterol"
                    isRequired={true}
                    label="HDL-cholesterol"
                    maximumFractionDigits={2}
                />
                <NumberInputColumnRowFormGroup
                    appendedLabel="mmol/l"
                    fieldKey="triglyceriden"
                    isRequired={true}
                    label="Triglyceriden"
                    maximumFractionDigits={2}
                />
                <LdlHdlRatio
                    hdlCholesterol={data.HDL_cholesterol}
                    ldlCholesterol={data.LDL_cholesterol}
                />
                <BooleanRadioGroupFormGroup
                    fieldKey="bijzonderheden_labwaarden"
                    isRequired={true}
                    label="Bijzonderheden labwaarden"
                    onChange={async (checked) =>
                        onChange(
                            {
                                bijzonderheden_labwaarden: checked,
                                bijzonderheden_labwaarden_omschrijving: null,
                            },
                            'bijzonderheden_labwaarden'
                        )
                    }
                    type="radio"
                />
                {data.bijzonderheden_labwaarden && (
                    <TextAreaFormGroup
                        fieldKey="bijzonderheden_labwaarden_omschrijving"
                        isRequired={true}
                        label="Omschrijving bijzonderheden"
                        placeholder="In de labwaarden staat..."
                    />
                )}
            </FormSection>
        </>
    );

    function loadNurseSpecialistMedScreeningCustomControlData(): void {
        const bariPreProcess = findLatestProcessByServiceCode(processes, ServiceCode.BariPre);

        if (!bariPreProcess) {
            return;
        }

        const customControlData = getCustomControlData<BariPreNurseSpecialistMedScreeningReportData>(
            bariPreProcess,
            CustomControlCode.BariPreNurseSpecialistMedScreeningReport,
            [ProcessActivityStatus.complete]
        );

        if (
            customControlData?.HDL_cholesterol ||
            customControlData?.LDL_cholesterol ||
            customControlData?.hba1c ||
            customControlData?.triglyceriden
        ) {
            onChange({
                HDL_cholesterol: customControlData.HDL_cholesterol,
                LDL_cholesterol: customControlData.LDL_cholesterol,
                hba1c: customControlData.hba1c,
                triglyceriden: customControlData.triglyceriden,
            });
            setIsPrefilled(true);
        }
    }
}
